/**
 * THE "IN A MINUTE" CARDS, ONE LOOK (owner, 23 Oct 2026: a dash of colour on today's layouts, mock-up v3). Each
 * module keeps its own words and layout; this is what they share: the navy rail down the card, the gradient book
 * chip and navy title, the point names and the one key figure in navy (the figure no larger than the text around
 * it), and one fold control, "Hide" / "Show", in every module.
 */
import React from 'react';
import { BookOpen } from 'lucide-react';
import IconTile from '../ui/IconTile.tsx';

/** The navy rail down the card's left edge. */
export const PRIMER_RAIL = 'border-l-4 border-l-brand-navy';
/** A point's name (the bold lead of each line). */
export const PRIMER_LEAD = 'text-brand-navy';
/** The one key figure in a line: navy, the same size as the text around it. */
export const PRIMER_FIGURE = 'font-semibold text-brand-navy';
/** The fold control's look; its words are always "Hide" (open) or "Show" (folded). */
export const PRIMER_TOGGLE = 'shrink-0 rounded-full border border-slate-300 bg-white px-3 py-1 text-[12.5px] font-semibold text-slate-600 hover:border-slate-400 hover:text-slate-900';

export const primerToggleWord = (open: boolean) => (open ? 'Hide' : 'Show');

/** The book chip and the title, as every primer starts. */
export const PrimerTitle = ({ children, as: Tag = 'h2' }: { children: React.ReactNode; as?: 'h2' | 'span' }) => (
    <span className="flex min-w-0 items-center gap-3">
        <IconTile icon={BookOpen} tone="brand" variant="solid" size="sm" />
        <Tag className="text-[19px] font-bold text-brand-navy">{children}</Tag>
    </span>
);

/**
 * The header row: chip, title, and the fold control. Folded, `aside` (the guide links) sits in the same row, so a
 * folded primer is one slim line rather than a near-empty card.
 */
export const PrimerHeader = ({ title, open, onToggle, aside = null, testId }: { title: React.ReactNode; open: boolean; onToggle: () => void; aside?: React.ReactNode; testId?: string }) => (
    <div className="flex flex-wrap items-center justify-between gap-3">
        <PrimerTitle>{title}</PrimerTitle>
        <span className="flex flex-wrap items-center gap-x-4 gap-y-1">
            {!open && aside}
            <button type="button" onClick={onToggle} aria-expanded={open} className={PRIMER_TOGGLE} data-testid={testId}>{primerToggleWord(open)}</button>
        </span>
    </div>
);

export default PrimerHeader;
